import React from 'react';
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import RowTypography from 'components/Table/RowTypography';

export type CellAlignment = 'left' | 'center' | 'right';

export interface MultiLevelHeaderCell {
	key: string;
	label: React.ReactNode;
	colSpan?: number;
	rowSpan?: number;
	backgroundColor?: string;
	color?: string;
	align?: CellAlignment;
	minWidth?: number | string;
	width?: number | string;
}

export interface MultiLevelTableColumn<T> {
	key: keyof T;
	align?: CellAlignment;
	minWidth?: number | string;
	width?: number | string;
	backgroundColor?: string;
	color?: string;
	fontWeight?: number | string;
	mergeRows?: boolean;
	render?: (
		value: T[keyof T],
		row: T,
		rowIndex: number,
	) => React.ReactNode;
}

interface MergedCell {
	rowSpan: number;
	hidden: boolean;
}

interface MultiLevelMergedTableProps<T> {
	headerRows: MultiLevelHeaderCell[][];
	columns: MultiLevelTableColumn<T>[];
	data: T[];
	getRowKey: (
		row: T,
		rowIndex: number,
	) => React.Key;
	isTotalRow?: (
		row: T,
		rowIndex: number,
	) => boolean;
	isSubtotalRow?: (
		row: T,
		rowIndex: number,
	) => boolean;
	bodyBackgroundColor?: string;
	subtotalRowBackgroundColor?: string;
	totalRowBackgroundColor?: string;
	bodyTextColor?: string;
	borderColor?: string;
	height?: number | string;
	stickyHeader?: boolean;
	frozenColumnCount?: number;
	emptyMessage?: string;
}

const FIRST_HEADER_ROW_HEIGHT = 26;

const formatCellValue = (
	value: unknown,
): React.ReactNode => {
	if (
		value === null ||
		value === undefined
	) {
		return '';
	}

	if (
		typeof value === 'string' ||
		typeof value === 'number'
	) {
		return value;
	}

	if (typeof value === 'boolean') {
		return value ? 'true' : 'false';
	}

	return '';
};

const renderTableValue = (
	value: React.ReactNode,
	fontWeight: number | string = 500,
) => (
	<RowTypography
		sx={{
			fontWeight: `${fontWeight} !important`,
			fontSize: '12px !important',
			overflow: 'hidden',
		}}
	>
		{value}
	</RowTypography>
);

const getHeaderRowKey = (
	headerRow: MultiLevelHeaderCell[],
): string => {
	const firstHeaderCell = headerRow[0];

	return firstHeaderCell?.key ?? 'empty-header-row';
};

const getHeaderTop = (
	stickyHeader: boolean,
	headerRowIndex: number,
): number | string => {
	if (!stickyHeader) {
		return 'auto';
	}

	if (headerRowIndex === 0) {
		return 0;
	}

	return FIRST_HEADER_ROW_HEIGHT;
};

const getHeaderZIndex = (
	stickyHeader: boolean,
	headerRowIndex: number,
	isFrozenColumn: boolean,
): number | string => {
	if (isFrozenColumn) {
		return 6;
	}

	if (!stickyHeader) {
		return 'auto';
	}

	if (headerRowIndex === 0) {
		return 4;
	}

	return 3;
};

const getHeaderHeight = (
	headerRowIndex: number,
): number | string => {
	if (headerRowIndex === 0) {
		return FIRST_HEADER_ROW_HEIGHT;
	}

	return 'auto';
};

const getHeaderPadding = (
	headerRowIndex: number,
): string => {
	if (headerRowIndex === 0) {
		return '2px 14px';
	}

	return '16px 14px 16px 24px';
};

const getRowBackgroundColor = (
	isTotal: boolean,
	isSubtotal: boolean,
	bodyBackgroundColor: string,
	subtotalRowBackgroundColor: string,
	totalRowBackgroundColor: string,
): string => {
	if (isTotal) {
		return totalRowBackgroundColor;
	}

	if (isSubtotal) {
		return subtotalRowBackgroundColor;
	}

	return bodyBackgroundColor;
};

const getCellBackgroundColor = (
	isTotal: boolean,
	isSubtotal: boolean,
	columnBackgroundColor: string | undefined,
	bodyBackgroundColor: string,
	subtotalRowBackgroundColor: string,
	totalRowBackgroundColor: string,
): string => {
	if (isTotal) {
		return totalRowBackgroundColor;
	}

	if (isSubtotal) {
		return subtotalRowBackgroundColor;
	}

	return columnBackgroundColor ?? bodyBackgroundColor;
};

const getCellFontWeight = (
	isTotal: boolean,
	isSubtotal: boolean,
	columnFontWeight: number | string | undefined,
): number | string => {
	if (isTotal || isSubtotal) {
		return 700;
	}

	return columnFontWeight ?? 500;
};

const getColumnWidth = <T,>(
	column: MultiLevelTableColumn<T>,
): number => {
	const columnWidth =
		column.width ?? column.minWidth ?? 0;

	if (typeof columnWidth === 'number') {
		return columnWidth;
	}

	const parsedWidth = Number.parseFloat(columnWidth);

	return Number.isFinite(parsedWidth)
		? parsedWidth
		: 0;
};

const getFrozenColumnLeft = <T,>(
	columns: MultiLevelTableColumn<T>[],
	columnIndex: number,
): number => {
	return columns
		.slice(0, columnIndex)
		.reduce(
			(totalWidth, column) =>
				totalWidth + getColumnWidth(column),
			0,
		);
};

const getHeaderCellStartIndex = (
	headerRow: MultiLevelHeaderCell[],
	headerCellIndex: number,
): number => {
	return headerRow
		.slice(0, headerCellIndex)
		.reduce(
			(totalColumns, headerCell) =>
				totalColumns +
				(headerCell.colSpan ?? 1),
			0,
		);
};

const getHeaderCellLeft = <T,>(
	columns: MultiLevelTableColumn<T>[],
	headerCellStartIndex: number,
): number => {
	return getFrozenColumnLeft(
		columns,
		headerCellStartIndex,
	);
};

const isFrozenHeaderCell = (
	headerCellStartIndex: number,
	headerCell: MultiLevelHeaderCell,
	frozenColumnCount: number,
): boolean => {
	const headerCellEndIndex =
		headerCellStartIndex +
		(headerCell.colSpan ?? 1);

	return (
		frozenColumnCount > 0 &&
		headerCellStartIndex < frozenColumnCount &&
		headerCellEndIndex <= frozenColumnCount
	);
};

const getMergedCell = <T,>(
	data: T[],
	rowIndex: number,
	columnIndex: number,
	columns: MultiLevelTableColumn<T>[],
): MergedCell => {
	const column = columns[columnIndex];

	if (!column.mergeRows) {
		return {
			rowSpan: 1,
			hidden: false,
		};
	}

	const currentValue = data[rowIndex][column.key];

	const precedingMergedColumnsMatch = (
		firstRow: T,
		secondRow: T,
	) =>
		columns
			.slice(0, columnIndex)
			.filter((item) => item.mergeRows)
			.every(
				(item) =>
					firstRow[item.key] ===
					secondRow[item.key],
			);

	if (
		rowIndex > 0 &&
		data[rowIndex - 1][column.key] ===
			currentValue &&
		precedingMergedColumnsMatch(
			data[rowIndex],
			data[rowIndex - 1],
		)
	) {
		return {
			rowSpan: 0,
			hidden: true,
		};
	}

	let rowSpan = 1;

	for (
		let nextRowIndex = rowIndex + 1;
		nextRowIndex < data.length;
		nextRowIndex += 1
	) {
		const nextRow = data[nextRowIndex];

		if (
			nextRow[column.key] !== currentValue ||
			!precedingMergedColumnsMatch(
				data[rowIndex],
				nextRow,
			)
		) {
			break;
		}

		rowSpan += 1;
	}

	return {
		rowSpan,
		hidden: false,
	};
};

const MultiLevelMergedTable = <T,>({
	headerRows,
	columns,
	data,
	getRowKey,
	isTotalRow,
	isSubtotalRow,
	bodyBackgroundColor = '#EAF3FA',
	subtotalRowBackgroundColor = '#909090',
	totalRowBackgroundColor = '#D5E7F5',
	bodyTextColor = '#000',
	borderColor = '#FFFFFF',
	height = 650,
	stickyHeader = true,
	frozenColumnCount = 0,
	emptyMessage = 'No data available',
}: Readonly<MultiLevelMergedTableProps<T>>) => {
	return (
		<TableContainer
			component={Box}
			className="multi-level-merged-table"
			sx={{
				width: '100%',
				height,
				maxHeight: height,
				overflowX: 'auto',
				overflowY: 'auto',
				border: `1px solid ${borderColor} !important`,
				borderRadius: '4px',
				background: `${bodyBackgroundColor} !important`,
				backgroundColor: `${bodyBackgroundColor} !important`,

				'&& .MuiTable-root': {
					background: `${bodyBackgroundColor} !important`,
					backgroundColor: `${bodyBackgroundColor} !important`,
				},

				'&& .MuiTableBody-root': {
					background: `${bodyBackgroundColor} !important`,
					backgroundColor: `${bodyBackgroundColor} !important`,
				},

				'&& .MuiTableBody-root .MuiTableRow-root': {
					height: '32px !important',
					minHeight: '32px !important',
					background: `${bodyBackgroundColor} !important`,
					backgroundColor: `${bodyBackgroundColor} !important`,
				},

				'&& .MuiTableBody-root .MuiTableCell-root': {
					padding: '6px !important',
					height: '32px !important',
					minHeight: '32px !important',
					borderRight: `1px solid ${borderColor} !important`,
					borderBottom: `1px solid ${borderColor} !important`,
				},

				'&& .MuiTableHead-root .MuiTableCell-root': {
					borderRight: `1px solid ${borderColor} !important`,
					borderBottom: `1px solid ${borderColor} !important`,
				},
				
				'&& .MuiTableCell-root, && .MuiTypography-root': {
					fontSize: '12px !important',
				},
			}}
		>
			<Table
				stickyHeader={stickyHeader}
				sx={{
					minWidth: 'max-content',
					borderCollapse: 'separate',
					borderSpacing: 0,
					tableLayout: 'auto',
					background: `${bodyBackgroundColor} !important`,
					backgroundColor: `${bodyBackgroundColor} !important`,
				}}
			>
				<TableHead>
					{headerRows.map(
						(
							headerRow,
							headerRowIndex,
						) => (
							<TableRow
								key={getHeaderRowKey(
									headerRow,
								)}
								sx={{
									height:
										getHeaderHeight(
											headerRowIndex,
										),
								}}
							>
								{headerRow.map(
									(
										headerCell,
										headerCellIndex,
									) => {
										const headerCellStartIndex =
											getHeaderCellStartIndex(
												headerRow,
												headerCellIndex,
											);

										const frozenHeaderCell =
											isFrozenHeaderCell(
												headerCellStartIndex,
												headerCell,
												frozenColumnCount,
											);

										const headerCellLeft =
											getHeaderCellLeft(
												columns,
												headerCellStartIndex,
											);

										return (
											<TableCell
												key={
													headerCell.key
												}
												colSpan={
													headerCell.colSpan ??
													1
												}
												rowSpan={
													headerCell.rowSpan ??
													1
												}
												align={
													headerCell.align ??
													'center'
												}
												sx={{
													position:
														stickyHeader ||
														frozenHeaderCell
															? 'sticky'
															: 'static',
													top: getHeaderTop(
														stickyHeader,
														headerRowIndex,
													),
													left:
														frozenHeaderCell
															? headerCellLeft
															: 'auto',
													height:
														getHeaderHeight(
															headerRowIndex,
														),
													background:
														`${headerCell.backgroundColor ?? '#003B63'} !important`,
													backgroundColor:
														`${headerCell.backgroundColor ?? '#003B63'} !important`,
													color:
														`${headerCell.color ?? '#FFFFFF'} !important`,
													fontWeight:
														500,
													fontSize:
														'12px',
													lineHeight:
														1.6,
                                                    textTransform: 'uppercase',
													width:
														headerCell.width,
													minWidth:
														headerCell.minWidth ??
														headerCell.width,
													padding:
														getHeaderPadding(
															headerRowIndex,
														),
													whiteSpace:
														'normal',
													verticalAlign:
														'middle',
													borderRight: `1px solid ${borderColor} !important`,
													borderBottom: `1px solid ${borderColor} !important`,
													zIndex:
														getHeaderZIndex(
															stickyHeader,
															headerRowIndex,
															frozenHeaderCell,
														),
												}}
											>
												{
													headerCell.label
												}
											</TableCell>
										);
									},
								)}
							</TableRow>
						),
					)}
				</TableHead>

				<TableBody
					sx={{
						background: `${bodyBackgroundColor} !important`,
						backgroundColor: `${bodyBackgroundColor} !important`,
					}}
				>
					{data.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={
									columns.length
								}
								align="center"
								sx={{
									padding: 3,
									color: `${bodyTextColor} !important`,
									background: `${bodyBackgroundColor} !important`,
									backgroundColor: `${bodyBackgroundColor} !important`,
									borderRight: `1px solid ${borderColor} !important`,
									borderBottom: `1px solid ${borderColor} !important`,
								}}
							>
								{renderTableValue(
									emptyMessage,
								)}
							</TableCell>
						</TableRow>
					) : (
						data.map(
							(row, rowIndex) => {
								const totalRow =
									isTotalRow?.(
										row,
										rowIndex,
									) ?? false;

								const subtotalRow =
									isSubtotalRow?.(
										row,
										rowIndex,
									) ?? false;

								const rowBackgroundColor =
									getRowBackgroundColor(
										totalRow,
										subtotalRow,
										bodyBackgroundColor,
										subtotalRowBackgroundColor,
										totalRowBackgroundColor,
									);

								const rowKey =
									getRowKey(
										row,
										rowIndex,
									);

								return (
									<TableRow
										key={rowKey}
										sx={{
											height:
												'32px',
											minHeight:
												'32px',
											background: `${rowBackgroundColor} !important`,
											backgroundColor: `${rowBackgroundColor} !important`,

											'&:hover': {
												background: `${rowBackgroundColor} !important`,
												backgroundColor: `${rowBackgroundColor} !important`,
											},
										}}
									>
										{columns.map(
											(
												column,
												columnIndex,
											) => {
												const mergedCell =
													getMergedCell(
														data,
														rowIndex,
														columnIndex,
														columns,
													);

												if (
													mergedCell.hidden
												) {
													return null;
												}

												const value =
													row[
														column
															.key
													];

												const cellBackgroundColor =
													getCellBackgroundColor(
														totalRow,
														subtotalRow,
														column.backgroundColor,
														bodyBackgroundColor,
														subtotalRowBackgroundColor,
														totalRowBackgroundColor,
													);

												const cellFontWeight =
													getCellFontWeight(
														totalRow,
														subtotalRow,
														column.fontWeight,
													);

												const frozenBodyColumn =
													columnIndex <
													frozenColumnCount;

												const frozenLeft =
													getFrozenColumnLeft(
														columns,
														columnIndex,
													);

												const cellValue =
													column.render
														? column.render(
																value,
																row,
																rowIndex,
															)
														: formatCellValue(
																value,
															);

												return (
													<TableCell
														key={`${String(
															column.key,
														)}-${String(
															rowKey,
														)}`}
														rowSpan={
															mergedCell.rowSpan
														}
														align={
															column.align ??
															'left'
														}
														sx={{
															position:
																frozenBodyColumn
																	? 'sticky'
																	: 'static',
															left:
																frozenBodyColumn
																	? frozenLeft
																	: 'auto',
															zIndex:
																frozenBodyColumn
																	? 2
																	: 'auto',
															background: `${cellBackgroundColor} !important`,
															backgroundColor: `${cellBackgroundColor} !important`,
															color: `${column.color ?? bodyTextColor} !important`,
															fontWeight:
																cellFontWeight,
															fontSize:
																'12px',
															lineHeight:
																1.4,
															width:
																column.width,
															minWidth:
																column.minWidth ??
																column.width,
															maxWidth:
																column.width,
															height:
																'32px',
															minHeight:
																'32px',
															padding:
																'6px 10px 6px 14px !important',
															boxSizing:
																'border-box',
															verticalAlign:
																'middle',
															whiteSpace:
																'pre-line',
															wordBreak:
																'normal',
															borderRight: `1px solid ${borderColor} !important`,
															borderBottom: `1px solid ${borderColor} !important`,
														}}
													>
														{renderTableValue(
															cellValue,
															cellFontWeight,
														)}
													</TableCell>
												);
											},
										)}
									</TableRow>
								);
							},
						)
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default MultiLevelMergedTable;